/**
 * Sonda de conexion del usuario que tiene el panel abierto.
 *
 * Vive en `PanelContainer`, no en la vista de red: para saber quien tuvo la
 * peor conexion del dia hay que medir todo el tiempo, no solo cuando
 * alguien se acuerda de mirar la pantalla.
 *
 * Dos capas:
 *
 *   1. Ping por el canal `network` cada 10s. Barato y continuo. Es TCP, asi
 *      que no puede ver perdida de paquetes: las retransmisiones la tapan y
 *      aparecen como picos de RTT. Por eso lo que cuenta son "cortes", no
 *      perdida.
 *   2. Sonda UDP por el TURN cada 12 minutos, que si mide perdida real sobre
 *      el path de la voz.
 *
 * Dos cosas que hay que hacer si o si, o el histórico se llena de mentiras:
 * no medir con la pestaña en segundo plano, porque Chrome baja los timers a
 * uno por minuto, y descartar la muestra posterior a una suspension del
 * equipo, que aparece como un RTT de decenas de segundos.
 */

import { Channel } from 'phoenix';
import WebSocket from '@/socket';
import BucketAccumulator, { BucketPayload, connectionHint } from './accumulator';
import { level, NetworkLevel } from './score';
import { runUdpProbe, UdpProbeResult } from './udp';

const PING_INTERVAL_MS = 10_000;
const PING_TIMEOUT_MS = 5_000;
const UDP_INTERVAL_MS = 12 * 60 * 1000;

/** Pings del boton "probar ahora" cuando la sonda UDP esta apagada. */
const BURST_PINGS = 5;
const BURST_SPACING_MS = 400;

/**
 * La capa 2 depende de un TURN alcanzable. Mientras no haya uno, queda
 * apagada: si no, la pantalla muestra un error permanente que no es
 * problema del cliente sino de nuestra infraestructura.
 *
 * Se prende con VUE_APP_NETWORK_UDP_PROBE=1 y no requiere ningun otro
 * cambio: el resto del camino ya esta escrito y probado.
 */
const udpEnabled = ['1', 'true'].includes(process.env.VUE_APP_NETWORK_UDP_PROBE || '');

/** Un hueco mayor a esto no es lentitud de red, es un equipo que desperto. */
const SLEEP_GAP_FACTOR = 3;

/**
 * Chrome baja los timers de una pestaña en segundo plano a uno por minuto.
 * Medir ahi produce cortes falsos todas las noches.
 */
function documentVisible(): boolean {
    return typeof document === 'undefined' || document.visibilityState === 'visible';
}

export interface LiveState {
    connected: boolean;
    samples: number;
    timeouts: number;
    reconnects: number;
    rtt: number | null;
    jitter: number | null;
    serverLoad: number | null;
    udp: UdpProbeResult | null;
    udpAt: number | null;
    udpRunning: boolean;
    udpEnabled: boolean;
    score: number | null;
    level: NetworkLevel;
    effectiveType: string | null;
    downlink: number | null;
}

type Listener = (state: LiveState) => void;

export default class NetworkProbe {
    private socket: WebSocket;

    private channel: Channel | null = null;

    private accumulator = new BucketAccumulator();

    private listeners: Listener[] = [];

    private pingTimer: number | null = null;

    private udpTimer: number | null = null;

    private lastTickAt = 0;

    private udpAt: number | null = null;

    private udpRunning = false;

    private connected = false;

    private stopped = true;

    /**
     * La primera muestra despues de un join se descarta.
     *
     * Phoenix encola el push hasta que el join termina, asi que ese RTT
     * incluye el armado del canal y no la red: aparecia como 1.2s cuando la
     * conexion real estaba en 120ms, y con pocas muestras en la ventana
     * arruinaba el promedio y el jitter.
     */
    private warmedUp = false;

    private onVisibility = () => {
        // al volver de segundo plano el proximo tick no puede compararse
        // contra el anterior: la pestaña estuvo congelada
        this.lastTickAt = Date.now();
    };

    constructor(socket: WebSocket) {
        this.socket = socket;
    }

    start(): void {
        if (!this.stopped) {
            return;
        }

        this.stopped = false;
        this.lastTickAt = Date.now();

        this.channel = this.socket.channel('network', {});
        this.channel
            .join()
            .receive('ok', () => {
                this.connected = true;
                this.emit();
                this.tick();
            })
            .receive('error', () => {
                this.connected = false;
                this.emit();
            });

        this.socket.onClose(() => {
            this.connected = false;
            this.warmedUp = false;
            this.push(this.accumulator.addReconnect(Date.now()));
            this.emit();
        });

        this.socket.onOpen(() => {
            this.connected = true;
            this.emit();
        });

        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.onVisibility);
        }

        this.pingTimer = window.setInterval(() => this.tick(), PING_INTERVAL_MS);

        if (udpEnabled) {
            this.udpTimer = window.setInterval(() => this.runUdp(), UDP_INTERVAL_MS);
            this.runUdp();
        }
    }

    stop(): void {
        if (this.stopped) {
            return;
        }

        this.stopped = true;
        this.warmedUp = false;

        if (this.pingTimer !== null) {
            window.clearInterval(this.pingTimer);
            this.pingTimer = null;
        }
        if (this.udpTimer !== null) {
            window.clearInterval(this.udpTimer);
            this.udpTimer = null;
        }
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.onVisibility);
        }

        this.push(this.accumulator.flush());

        if (this.channel) {
            this.channel.leave();
            this.channel = null;
        }
    }

    onChange(listener: Listener): () => void {
        this.listeners.push(listener);
        listener(this.state());

        return () => {
            this.listeners = this.listeners.filter((item) => item !== listener);
        };
    }

    /**
     * Boton "probar ahora". Con la sonda UDP prendida corre una sonda
     * completa; apagada dispara una rafaga de pings, que refresca latencia
     * y jitter en el momento en vez de esperar el proximo tick de 10s.
     */
    async testNow(): Promise<void> {
        if (udpEnabled) {
            await this.runUdp(true);
            return;
        }

        await this.burst();
    }

    state(): LiveState {
        const { live } = this.accumulator;
        const hint = connectionHint();

        return {
            connected: this.connected,
            samples: live.samples,
            timeouts: live.timeouts,
            reconnects: live.reconnects,
            rtt: live.rtt,
            jitter: live.jitter,
            serverLoad: live.serverLoad,
            udp: live.udp,
            udpAt: this.udpAt,
            udpRunning: this.udpRunning,
            udpEnabled,
            score: live.score,
            level: level(live.score),
            effectiveType: hint.effectiveType,
            downlink: hint.downlink,
        };
    }

    private tick(): void {
        if (this.stopped || !this.channel || !documentVisible()) {
            this.lastTickAt = Date.now();
            return;
        }

        const now = Date.now();
        const gap = now - this.lastTickAt;
        this.lastTickAt = now;

        // el equipo estuvo suspendido: se saltea el tick entero, incluido el
        // conteo de cortes, porque no hubo red que medir
        if (gap > PING_INTERVAL_MS * SLEEP_GAP_FACTOR) {
            return;
        }

        const sentAt = performance.now();

        this.channel
            .push('ping', { t: now }, PING_TIMEOUT_MS)
            .receive('ok', (reply: { rq?: number }) => {
                if (!this.warmedUp) {
                    this.warmedUp = true;
                    this.emit();
                    return;
                }

                const rtt = Math.round(performance.now() - sentAt);
                const load = typeof reply?.rq === 'number' ? reply.rq : null;

                this.push(this.accumulator.addSample(rtt, load, Date.now()));
                this.emit();
            })
            .receive('timeout', () => {
                this.push(this.accumulator.addTimeout(Date.now()));
                this.emit();
            })
            .receive('error', () => {
                this.push(this.accumulator.addTimeout(Date.now()));
                this.emit();
            });
    }

    private async burst(): Promise<void> {
        if (this.stopped) {
            return;
        }

        this.udpRunning = true;
        this.emit();

        for (let i = 0; i < BURST_PINGS; i += 1) {
            this.tick();
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => { setTimeout(resolve, BURST_SPACING_MS); });
        }

        this.udpRunning = false;
        this.emit();
    }

    private async runUdp(force = false): Promise<void> {
        if (!udpEnabled || this.udpRunning || this.stopped) {
            return;
        }
        if (!force && !documentVisible()) {
            return;
        }

        this.udpRunning = true;
        this.emit();

        const result = await runUdpProbe();

        this.udpRunning = false;
        this.udpAt = Date.now();
        this.push(this.accumulator.setUdp(result, this.udpAt));
        this.emit();
    }

    private push(payload: BucketPayload | null): void {
        if (!payload || !this.channel) {
            return;
        }

        // best effort: si se pierde una ventana el panel sigue andando
        this.channel.push('bucket', payload as unknown as object, PING_TIMEOUT_MS);
    }

    private emit(): void {
        const state = this.state();
        this.listeners.forEach((listener) => listener(state));
    }
}
